import { SystemSettings, DEFAULT_SETTINGS } from '../types/settings';
import { AudioFrameConstants } from '../constants/AudioFrameConstants';

const SETTINGS_STORAGE_KEY = 'wavelink_settings';

export class SettingsService {
  private static instance: SettingsService;
  private settings: SystemSettings;

  private constructor() {
    this.settings = this.loadSettings();
  }

  public static getInstance(): SettingsService {
    if (!SettingsService.instance) {
      SettingsService.instance = new SettingsService();
    }
    return SettingsService.instance;
  }

  /**
   * 从localStorage加载设置
   */
  private loadSettings(): SystemSettings {
    try {
      const stored = localStorage.getItem(SETTINGS_STORAGE_KEY);
      if (stored) {
        const parsed = JSON.parse(stored);
        // 合并默认设置和存储的设置，确保所有字段都存在
        return this.mergeWithDefaults(parsed);
      }
    } catch (error) {
      console.warn('SettingsService: 加载设置失败，使用默认设置:', error);
    }
    return DEFAULT_SETTINGS;
  }

  /**
   * 将用户设置与默认设置合并
   */
  private mergeWithDefaults(userSettings: Partial<SystemSettings>): SystemSettings {
    return {
      network: { ...DEFAULT_SETTINGS.network, ...userSettings.network },
      audio: { ...DEFAULT_SETTINGS.audio, ...userSettings.audio },
      deviceMonitoring: { ...DEFAULT_SETTINGS.deviceMonitoring, ...userSettings.deviceMonitoring },
      ptt: { ...DEFAULT_SETTINGS.ptt, ...userSettings.ptt }
    };
  }

  /**
   * 保存设置到localStorage
   */
  private saveSettings(): void {
    try {
      localStorage.setItem(SETTINGS_STORAGE_KEY, JSON.stringify(this.settings));
      console.log('SettingsService: 设置已保存');
    } catch (error) {
      console.error('SettingsService: 保存设置失败:', error);
    }
  }

  /**
   * 获取所有设置
   */
  public getSettings(): SystemSettings {
    return { ...this.settings };
  }

  /**
   * 更新设置
   */
  public updateSettings(newSettings: Partial<SystemSettings>): void {
    this.settings = this.mergeWithDefaults({ ...this.settings, ...newSettings });
    this.saveSettings();
  }

  /**
   * 重置为默认设置
   */
  public resetToDefaults(): void {
    this.settings = DEFAULT_SETTINGS;
    this.saveSettings();
    console.log('SettingsService: 已重置为默认设置');
  }

  /**
   * 获取网络设置
   */
  public getNetworkSettings() {
    return this.settings.network;
  }

  /**
   * 获取音频设置
   */
  public getAudioSettings() {
    return this.settings.audio;
  }

  /**
   * 获取设备监控设置
   */
  public getDeviceMonitoringSettings() {
    return this.settings.deviceMonitoring;
  }

  /**
   * 获取PTT设置
   */
  public getPttSettings() {
    return this.settings.ptt;
  }

  /**
   * 更新网络设置
   */
  public updateNetworkSettings(networkSettings: Partial<SystemSettings['network']>): void {
    this.settings.network = { ...this.settings.network, ...networkSettings };
    this.saveSettings();
  }

  /**
   * 更新音频设置
   */
  public updateAudioSettings(audioSettings: Partial<SystemSettings['audio']>): void {
    this.settings.audio = { ...this.settings.audio, ...audioSettings };
    this.saveSettings();
  }
  
  /**
   * 强制修复音频缓冲区大小
   * 确保缓冲区大小是2的幂次方且大于设备帧大小
   */
  public fixAudioBufferSize(): void {
    const currentBufferSize = this.settings.audio.bufferSize;
    
    // 检查当前缓冲区大小是否有效
    if (currentBufferSize < AudioFrameConstants.DEVICE_FRAME_SIZE || !this.isPowerOfTwo(currentBufferSize)) {
      console.log(`SettingsService: 检测到无效的缓冲区大小 ${currentBufferSize}，修复为${AudioFrameConstants.MIN_PACKET_SIZE}`);
      this.settings.audio.bufferSize = AudioFrameConstants.MIN_PACKET_SIZE;
      this.saveSettings();
    }
  }
  
  /**
   * 检查数字是否为2的幂次方
   */
  private isPowerOfTwo(n: number): boolean {
    return n > 0 && (n & (n - 1)) === 0;
  }

  /**
   * 更新设备监控设置
   */
  public updateDeviceMonitoringSettings(monitoringSettings: Partial<SystemSettings['deviceMonitoring']>): void {
    this.settings.deviceMonitoring = { ...this.settings.deviceMonitoring, ...monitoringSettings };
    this.saveSettings();
  }

  /**
   * 更新PTT设置
   */
  public updatePttSettings(pttSettings: Partial<SystemSettings['ptt']>): void {
    this.settings.ptt = { ...this.settings.ptt, ...pttSettings };
    this.saveSettings();
  }
}

// 导出单例实例
export const settingsService = SettingsService.getInstance();
